import { useState, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { useCentralConsumer } from '../../hooks/useCentralConsumer';
import { useUsageAllowance } from '../../hooks/useUsageAllowance';
import { allowanceText, isKnownExhausted, serverMessageOf, type AllowanceView } from '../../utils/billingStatusView';
import { useLazyPreviewClipQuery, useLazyListenToClipQuery } from '../../store/api/portfolioApi';
import {
  useSearchMarketplaceProfilesQuery,
  type MarketplaceProfile,
} from '../../store/api/marketplaceApi';
import { formatDuration } from '../../utils/pitchDuration';

/**
 * A displayable pitch item derived from a MarketplaceProfile + one of its featured clips.
 */
export interface DisplayPitch {
  id: string;
  slug: string;
  clipId: string;
  title: string;
  author: string;
  /** null when the clip's duration was not measured (never shown as 0:00). */
  duration: string | null;
  durationSeconds: number | null;
  clipType: string;
  overallScore: number | null;
  /** null when the person hides their authority section. */
  sessionCount: number | null;
}


function mapProfileClipsToPitches(profiles: MarketplaceProfile[]): DisplayPitch[] {
  const pitches: DisplayPitch[] = [];
  for (const profile of profiles) {
    const slug = profile.publicSlug;
    if (!slug) continue;
    const clips = profile.featuredClipsSummaryJson || [];
    for (const clip of clips) {
      pitches.push({
        id: `${profile.userId}-${clip.id}`,
        slug,
        clipId: clip.id,
        title: clip.title,
        author: profile.displayName || '',
        duration: formatDuration(clip.durationSeconds),
        durationSeconds: typeof clip.durationSeconds === 'number' && clip.durationSeconds > 0 ? clip.durationSeconds : null,
        clipType: clip.clipType,
        overallScore: profile.overallScore ?? null,
        sessionCount: profile.sessionCount,
      });
    }
  }
  return pitches;
}

const CLIP_TYPE_FILTERS = ['All', 'assessment', 'practice', 'coaching'];
const CLIP_TYPE_LABELS: Record<string, string> = {
  All: 'All',
  assessment: 'Assessment',
  practice: 'Practice',
  coaching: 'Coaching',
};

const SORT_OPTIONS = [
  { value: 'score', label: 'Highest Score', sortBy: 'overallScore' as const, sortOrder: 'DESC' as const },
  { value: 'recent', label: 'Most Recent', sortBy: 'rankingScore' as const, sortOrder: 'DESC' as const },
  { value: 'sessions', label: 'Most Sessions', sortBy: 'sessionCount' as const, sortOrder: 'DESC' as const },
];

const PAGE_SIZE = 20;

export function PitchesPage() {
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [sortBy, setSortBy] = useState('score');
  const [selectedPitchId, setSelectedPitchId] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  // Plan (Central capability) for feature gating; the allowance is display only.
  const { hasCapability, bootCompleted } = useCentralConsumer();
  const isPaid = hasCapability('marketplace', 'listen');
  const isFree = !isPaid;
  const isPlanLoading = !bootCompleted;
  const { listen: listenAllowance, refetch: refetchAllowance } = useUsageAllowance({ skip: !isPaid });
  const listenText = allowanceText(listenAllowance);

  // Map frontend sort key to backend params
  const sortOption = SORT_OPTIONS.find((o) => o.value === sortBy) || SORT_OPTIONS[0];

  // Build query params — filter, sort, and pagination all hit the backend
  const queryParams = useMemo(() => ({
    limit: PAGE_SIZE,
    offset,
    sortBy: sortOption.sortBy,
    sortOrder: sortOption.sortOrder,
    ...(selectedFilter !== 'All' && { clipType: selectedFilter as 'assessment' | 'practice' | 'coaching' }),
  }), [offset, sortOption.sortBy, sortOption.sortOrder, selectedFilter]);

  // Fetch marketplace profiles with clips — distinct RTK cache key per filter/sort/page
  const { data: marketplaceData, isLoading, isFetching, error } = useSearchMarketplaceProfilesQuery(
    queryParams,
    { refetchOnMountOrArgChange: true }
  );

  // Map profiles to displayable pitch items (backend already filtered + sorted)
  const pitches = useMemo(() => {
    if (!marketplaceData?.profiles) return [];
    return mapProfileClipsToPitches(marketplaceData.profiles);
  }, [marketplaceData]);

  const total = marketplaceData?.total ?? 0;
  const hasMore = offset + PAGE_SIZE < total;

  // Reset offset when filter or sort changes
  const handleFilterChange = (filter: string) => {
    setSelectedFilter(filter);
    setOffset(0);
  };
  const handleSortChange = (value: string) => {
    setSortBy(value);
    setOffset(0);
  };

  const selectedPitch = selectedPitchId ? pitches.find((p) => p.id === selectedPitchId) : null;

  return (
    <div className="space-y-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500">
        <Link to="/marketplace" className="hover:text-primary-600">Marketplace</Link>
        <span>/</span>
        <span className="text-gray-900">Sample Calls</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sample Calls</h1>
          <p className="mt-1 text-gray-500">
            Hear how top performers handle real scenarios
          </p>
        </div>

        <div className="flex items-center gap-4">
          {/* Monthly full-listen allowance (display only) */}
          {isPaid && !isPlanLoading && listenText && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 rounded-lg" data-testid="listen-allowance">
              <CreditIcon className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-medium text-primary-700">
                Full listens: {listenText}
              </span>
            </div>
          )}

          {/* Sort dropdown */}
          <select
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none"
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Clip type filters */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {CLIP_TYPE_FILTERS.map((filter) => (
          <button
            key={filter}
            onClick={() => handleFilterChange(filter)}
            className={clsx(
              'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
              selectedFilter === filter
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            {CLIP_TYPE_LABELS[filter] || filter}
          </button>
        ))}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="card-body">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gray-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error state */}
      {error && !isLoading && (
        <div className="text-center py-12">
          <p className="text-red-500">Failed to load pitches. Please try again later.</p>
        </div>
      )}

      {/* Pitch list */}
      {!isLoading && !error && (
        <div className="space-y-4">
          {pitches.map((pitchItem) => (
            <PitchCard
              key={pitchItem.id}
              pitch={pitchItem}
              onPlay={() => setSelectedPitchId(pitchItem.id)}
            />
          ))}
        </div>
      )}

      {!isLoading && !error && pitches.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <p>No sample calls found{selectedFilter !== 'All' ? ' for this category' : ''}.</p>
          <p className="text-sm mt-1">
            Sample calls appear when users publish their profiles with featured examples.
          </p>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && !error && pitches.length > 0 && (
        <div className="flex items-center justify-between pt-4">
          <p className="text-sm text-gray-500">
            Showing {offset + 1}&ndash;{Math.min(offset + PAGE_SIZE, total)} of {total}
          </p>
          <div className="flex gap-2">
            {offset > 0 && (
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={isFetching}
                className="btn-secondary text-sm disabled:opacity-50"
              >
                Previous
              </button>
            )}
            {hasMore && (
              <button
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={isFetching}
                className="btn-primary text-sm disabled:opacity-50"
              >
                {isFetching ? 'Loading...' : 'Next'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Pitch player modal */}
      {selectedPitch && (
        <PitchPlayerModal
          pitch={selectedPitch}
          onClose={() => setSelectedPitchId(null)}
          isPaid={isPaid}
          isFree={isFree}
          listenAllowance={listenAllowance}
          onListened={refetchAllowance}
        />
      )}
    </div>
  );
}

interface PitchCardProps {
  pitch: DisplayPitch;
  onPlay: () => void;
}

function PitchCard({ pitch, onPlay }: PitchCardProps) {
  return (
    <div className="card hover:shadow-md transition-shadow">
      <div className="card-body">
        <div className="flex items-start gap-4">
          {/* Play button */}
          <button
            onClick={onPlay}
            className="flex-shrink-0 w-14 h-14 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center hover:bg-primary-200 transition-colors"
          >
            <PlayIcon className="w-6 h-6 ml-1" />
          </button>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-gray-900">{pitch.title}</h3>
                <p className="text-sm text-gray-500 mt-0.5">by {pitch.author}</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-3 text-sm">
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                {CLIP_TYPE_LABELS[pitch.clipType] || pitch.clipType}
              </span>
              {pitch.duration && <span className="text-gray-400">{pitch.duration}</span>}
            </div>
          </div>

          {/* Stats */}
          <div className="hidden md:flex flex-col items-end gap-2">
            {pitch.overallScore != null && (
              <div className="flex items-center gap-1">
                <StarIcon className="w-4 h-4 text-yellow-400" />
                <span className="font-medium text-gray-900">{pitch.overallScore}</span>
              </div>
            )}
            {pitch.sessionCount != null && (
              <div className="text-sm text-gray-500">
                {pitch.sessionCount} sessions
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export interface PitchPlayerModalProps {
  pitch: DisplayPitch;
  onClose: () => void;
  isPaid: boolean;
  isFree: boolean;
  /** Display only — never gates Play Full (the server admits or refuses). */
  listenAllowance: AllowanceView;
  onListened: () => void;
}

export function PitchPlayerModal({
  pitch,
  onClose,
  isPaid,
  isFree,
  listenAllowance,
  onListened,
}: PitchPlayerModalProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playMode, setPlayMode] = useState<'snippet' | 'full' | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [fetchPreview] = useLazyPreviewClipQuery();
  const [fetchFullClip] = useLazyListenToClipQuery();

  const canPlayFull = isPaid;
  const snippetDuration = '0:30';

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  const playAudioUrl = useCallback((url: string) => {
    stopAudio();
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onended = () => { setIsPlaying(false); setPlayMode(null); };
    audio.onerror = () => { setAudioError('Failed to play audio'); setIsPlaying(false); };
    audio.play().catch(() => { setAudioError('Audio playback blocked by browser'); setIsPlaying(false); });
  }, [stopAudio]);

  const handlePlaySnippet = async () => {
    setPlayMode('snippet');
    setIsPlaying(true);
    setAudioError(null);

    try {
      const result = await fetchPreview({ slug: pitch.slug, clipId: pitch.clipId }).unwrap();
      if (result.previewUrl) {
        playAudioUrl(result.previewUrl);
      } else {
        setAudioError('Preview not available for this clip');
        setIsPlaying(false);
      }
    } catch {
      setAudioError('Could not load preview. Clip may not be available yet.');
      setIsPlaying(false);
    }
  };

  const handlePlayFull = async () => {
    if (!canPlayFull) return;
    setPlayMode('full');
    setIsPlaying(true);
    setAudioError(null);

    try {
      const result = await fetchFullClip({ slug: pitch.slug, clipId: pitch.clipId }).unwrap();
      if (result.audioUrl) {
        playAudioUrl(result.audioUrl);
        onListened();
      } else {
        setAudioError('Full audio not available');
        setIsPlaying(false);
      }
    } catch (err) {
      // The server is authoritative: show its refusal (limit reached, 503, ...) as sent.
      setAudioError(serverMessageOf(err) ?? 'Could not load the full clip. Please try again.');
      setIsPlaying(false);
      onListened();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
          >
            <CloseIcon className="w-6 h-6" />
          </button>

          {/* Player area */}
          <div className="aspect-video bg-gradient-to-br from-primary-500 to-primary-700 rounded-t-xl flex items-center justify-center relative">
            {/* Plan badge */}
            {isFree && (
              <div className="absolute top-4 left-4 px-2 py-1 bg-white/20 backdrop-blur rounded text-xs text-white">
                Preview Only
              </div>
            )}
            {isPaid && allowanceText(listenAllowance) && (
              <div className="absolute top-4 left-4 px-2 py-1 bg-white/20 backdrop-blur rounded text-xs text-white flex items-center gap-1">
                <CreditIcon className="w-3 h-3" />
                Full listens: {allowanceText(listenAllowance)}
              </div>
            )}

            <div className="text-center text-white">
              {!isPlaying ? (
                <>
                  {/* Play options */}
                  <div className="flex flex-col gap-3">
                    <button
                      className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors mx-auto"
                      onClick={handlePlaySnippet}
                    >
                      <PlayIcon className="w-8 h-8 ml-1" />
                    </button>
                    <p className="text-sm text-white/80">
                      {isFree ? `Preview (${snippetDuration})` : pitch.duration ? `Full (${pitch.duration})` : 'Full recording'}
                    </p>
                  </div>
                </>
              ) : (
                <div>
                  <button
                    className="w-16 h-16 rounded-full bg-white/30 flex items-center justify-center hover:bg-white/40 transition-colors mx-auto"
                    onClick={stopAudio}
                  >
                    <PauseIcon className="w-8 h-8" />
                  </button>
                  <p className="text-sm text-white/80 mt-3">
                    {playMode === 'snippet' ? `Preview: ${snippetDuration}` : pitch.duration ?? 'Full recording'}
                  </p>
                </div>
              )}
              {audioError && (
                <p className="text-sm text-red-200 mt-2">{audioError}</p>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{pitch.title}</h2>
                <p className="text-gray-500 mt-1">
                  {CLIP_TYPE_LABELS[pitch.clipType] || pitch.clipType} clip
                </p>
              </div>
              {pitch.overallScore != null && (
                <div className="flex items-center gap-1">
                  <StarIcon className="w-5 h-5 text-yellow-400" />
                  <span className="font-bold text-gray-900">{pitch.overallScore}</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 mt-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                <span className="text-white font-medium">{pitch.author[0]}</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">{pitch.author}</p>
                {pitch.sessionCount != null && (
                  <p className="text-sm text-gray-500">{pitch.sessionCount} sessions</p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              <span className="px-3 py-1 bg-primary-50 text-primary-700 text-sm rounded-full">
                {CLIP_TYPE_LABELS[pitch.clipType] || pitch.clipType}
              </span>
              {pitch.duration && (
                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                  {pitch.duration}
                </span>
              )}
            </div>

            {/* Free user upsell */}
            {isFree && (
              <div className="mt-4 p-4 bg-gradient-to-r from-primary-50 to-purple-50 rounded-lg border border-primary-100">
                <div className="flex items-start gap-3">
                  <LockIcon className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Unlock Full Access</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Upgrade to a paid plan to listen to full recordings and access all examples.
                    </p>
                    <Link
                      to="/settings/billing"
                      className="inline-block mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      View Plans &rarr;
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Allowance KNOWN to be used up (never shown when unknown) */}
            {isPaid && isKnownExhausted(listenAllowance) && (
              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-100">
                <div className="flex items-start gap-3">
                  <CreditIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Monthly Listening Allowance Used</p>
                    <p className="text-sm text-gray-600 mt-1">
                      You've used this month's full-listen allowance. It resets at the start of next month.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t bg-gray-50 rounded-b-xl">
            {isFree ? (
              <div className="flex gap-3">
                <button onClick={handlePlaySnippet} className="btn-secondary flex-1">
                  Play Preview ({snippetDuration})
                </button>
                <Link to="/settings/billing" className="btn-primary flex-1 text-center">
                  Upgrade for Full Access
                </Link>
              </div>
            ) : (
              <div className="flex gap-3">
                <button onClick={handlePlaySnippet} className="btn-secondary flex-1">
                  Preview ({snippetDuration})
                </button>
                {/* Never disabled on a client-side guess: the server admits or refuses. */}
                <button onClick={handlePlayFull} className="btn-primary flex-1" data-testid="play-full">
                  Play Full
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Icon components
function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function CreditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  );
}

function PauseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
    </svg>
  );
}
